// View memory (#2771): Back goes to exactly where the person started. A list screen keeps a small snapshot of itself (search, lane, filters, the open card, ...) and, when
// a detail is opened from it, a RETURN record (which row, how far the page and the table were scrolled). When the list is built again (header arrow, "Back to Contacts",
// breadcrumb parent, browser Back: they all just mount the list again) it takes the record and puts the snapshot, the scroll and the row back. Nothing here knows which
// Back control was used, so none of them can land somewhere else.
//
// sessionStorage, per signed-in user, always in try/catch (private mode, quota): a failure just means the list starts fresh, as it always did.
// A fresh visit from the sidebar menu never restores: the menu click clears the records (noteMenuNavigation). A deep link with no record behaves as before.
const PREFIX = 'charlie:view:';
const MAX_AGE_MS = 30 * 60 * 1000; // a return record older than this is stale: someone came back much later
const MAX_BYTES = 60000; // a snapshot larger than this is not kept (the list starts fresh rather than filling the storage)

let currentUser = 'anon';
const mem = new Map(); // the same data in memory, so a browser without storage still works inside one page life

export function setViewMemoryUser(id) { currentUser = id ? String(id) : 'anon'; }
const keyOf = (screen) => `${PREFIX}${currentUser}:${screen}`;

function read(screen) {
  const k = keyOf(screen);
  if (mem.has(k)) return mem.get(k);
  try {
    const raw = window.sessionStorage.getItem(k);
    if (raw) { const v = JSON.parse(raw); if (v && typeof v === 'object') { mem.set(k, v); return v; } }
  } catch (e) { /* no storage, or damaged: start fresh */ }
  return null;
}

function write(screen, value) {
  const k = keyOf(screen);
  if (!value) { mem.delete(k); try { window.sessionStorage.removeItem(k); } catch (e) { /* no storage */ } return; }
  let text = null;
  try { text = JSON.stringify(value); } catch (e) { return; }
  if (text.length > MAX_BYTES) return;
  mem.set(k, value);
  try { window.sessionStorage.setItem(k, text); } catch (e) { /* no storage or full: memory copy still serves this page life */ }
}

// The screen's current state, merged shallowly into what is kept. Cheap enough to call on every change.
export function rememberSnapshot(screen, snap) {
  if (!screen) return;
  const cur = read(screen) || {};
  write(screen, { ...cur, snap: { ...(cur.snap || {}), ...snap } });
}

export function getSnapshot(screen) {
  const cur = screen ? read(screen) : null;
  return (cur && cur.snap) || null;
}

// A detail is being opened (or the person is leaving the list some other way): remember where they were. info: { rowId, mainScroll, tableScroll, ... }.
export function recordReturn(screen, info) {
  if (!screen) return;
  const cur = read(screen) || {};
  write(screen, { ...cur, ret: { ...(cur.ret || {}), ...(info || {}), at: Date.now() } }); // a later record adds to an earlier one (a row click's table scroll survives the leave)
}

// What a list that is being built should put back: { snap, ret } when a return record is waiting and fresh, else null. Peeking does not consume (a double render is fine);
// the list calls consumeReturn once it has used it.
export function peekReturn(screen, now = Date.now()) {
  const cur = screen ? read(screen) : null;
  if (!cur || !cur.ret) return null;
  if (now - Number(cur.ret.at || 0) > MAX_AGE_MS) return null;
  return { snap: cur.snap || null, ret: cur.ret };
}

export function consumeReturn(screen) {
  const cur = screen ? read(screen) : null;
  if (!cur || !cur.ret) return;
  const { ret, ...rest } = cur; // eslint-disable-line no-unused-vars
  write(screen, rest);
}

export function forgetView(screen) { if (screen) write(screen, null); }

let menuNavAt = 0;
let menuNavPending = false; // a menu click happened and the screen it replaces has not been unmounted yet (a lazy page can take seconds to arrive)
const MENU_NAV_SETTLE_MS = 15000; // a click that never changed the page (a re-click of the same item) stops suppressing after this
const menuResets = new Set(); // callbacks that also forget what a menu click must not keep (the table's kept page and sort)

// A part of the app that keeps its own place (the table's page and sort) registers here so a sidebar click clears that too.
export function onMenuNavigation(fn) { menuResets.add(fn); return () => menuResets.delete(fn); }

// The page behind a menu click has been replaced (App calls this when the active page changes, after the old screen's cleanup ran).
export function menuNavigationSettled() { menuNavPending = false; }

// Recording on the way OUT of a list (its screen unmounts because the person opened something else): skipped when the cause is a sidebar menu click, which is a fresh visit.
export function recordReturnOnLeave(screen, info, now = Date.now()) {
  if (menuNavPending && now - menuNavAt < MENU_NAV_SETTLE_MS) return;
  if (now - menuNavAt < 1500) return;
  recordReturn(screen, info);
}

// A sidebar menu click is a fresh visit: every waiting return record is dropped so the page opens as it always did. (Snapshots stay: they are only ever used with a record.)
export function noteMenuNavigation() {
  menuNavAt = Date.now();
  menuNavPending = true;
  menuResets.forEach((fn) => { try { fn(); } catch (e) { /* a reset that fails must not block the click */ } });
  const prefix = `${PREFIX}${currentUser}:`;
  const names = new Set();
  mem.forEach((_, k) => { if (k.startsWith(prefix)) names.add(k); });
  try {
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const k = window.sessionStorage.key(i);
      if (k && k.startsWith(prefix)) names.add(k);
    }
  } catch (e) { /* no storage */ }
  names.forEach((k) => {
    const screen = k.slice(prefix.length);
    consumeReturn(screen);
  });
}

// Test and sign-out helper: drop everything this module holds, in memory and in this tab's storage (only its own keys).
export function resetViewMemory() {
  mem.clear();
  menuNavPending = false;
  menuNavAt = 0;
  try {
    const drop = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const k = window.sessionStorage.key(i);
      if (k && k.startsWith(PREFIX)) drop.push(k);
    }
    drop.forEach((k) => window.sessionStorage.removeItem(k));
  } catch (e) { /* no storage */ }
}

// ---- scroll helpers: the app's main region and a table's own scroller ----
export const MAIN_SCROLLER = '.charlie-main-content';

export function readMainScroll(doc = typeof document !== 'undefined' ? document : null) {
  try {
    const el = doc && doc.querySelector(MAIN_SCROLLER);
    return el ? el.scrollTop : 0;
  } catch (e) { return 0; }
}

export function applyMainScroll(top, doc = typeof document !== 'undefined' ? document : null) {
  try {
    const el = doc && doc.querySelector(MAIN_SCROLLER);
    if (el && Number.isFinite(Number(top))) el.scrollTop = Number(top);
  } catch (e) { /* nothing to scroll */ }
}
